import React from 'react';

/**
 * Landmark Component - Shows country's famous landmark that degrades as population depletes
 * 
 * NEW: Fortification Buffer System!
 * - When population > initial: Shows fortifications (sandbags, trenches, barbed wire)
 * - Fortifications absorb damage first, then landmark starts degrading
 * - Visual stages:
 *   - Buffer 60%+: Heavy fortifications (sandbags + trenches + barbed wire)
 *   - Buffer 30-60%: Medium fortifications (sandbags + trenches)
 *   - Buffer 0-30%: Light fortifications (sandbags only)
 * 
 * Degradation stages (when pop <= initial):
 * - 70-100%: Pristine (stage 0)
 * - 30-69%: Damaged (stage 1)
 * - 0-29%: Destroyed (stage 2)
 */
export default function Landmark({ country, initialPopulation }) {
  const currentPopulation = country.resources?.population || 0;
  
  // Calculate buffer (excess population above baseline)
  const bufferPopulation = Math.max(0, currentPopulation - initialPopulation);
  const bufferPercent = initialPopulation > 0 ? (bufferPopulation / initialPopulation) * 100 : 0;
  
  // Calculate landmark damage (only counts when below initial population)
  const populationPercent = initialPopulation > 0 ? Math.min(100, (currentPopulation / initialPopulation) * 100) : 100;
  
  // Determine if we show fortifications or landmark damage
  const hasBuffer = bufferPercent > 0;
  
  // Fortification levels based on buffer percentage
  let fortificationLevel = 0; // No fortifications
  if (bufferPercent > 60) {
    fortificationLevel = 3; // Heavy: sandbags + trenches + barbed wire
  } else if (bufferPercent > 30) {
    fortificationLevel = 2; // Medium: sandbags + trenches
  } else if (bufferPercent > 0) {
    fortificationLevel = 1; // Light: sandbags only
  }
  
  // Landmark degradation stage (only applies when no buffer)
  let stage = 0; // pristine
  if (!hasBuffer) {
    if (populationPercent < 30) {
      stage = 2; // destroyed
    } else if (populationPercent < 70) {
      stage = 1; // damaged
    }
  }
  
  // Landmark images by country code
  const landmarks = {
    USA: {
      name: "U.S. Capitol",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/7qdvyva1_Capitol-whole.png', // Pristine (70-100% population)
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/42d03rdp_Capitol-half.png', // Damaged (30-69% population)
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/bmjbulce_Capitol-gone.png' // Destroyed (0-29% population)
      ],
      fortifiedImages: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/0ia48l93_USA%20landmark%20buffered-1.png', // Light fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/9ktpyn6e_USA%20landmark%20buffered-2.png', // Medium fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/seuey80t_USA%20landmark%20buffered-3.png'  // Heavy fortifications
      ],
      position: { x: 25, y: 35 } // Position on map (percentage from left/top)
    },
    RUS: {
      name: "Spasskaya Tower",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/8oxw0s33_Spasskaya-whole.png', // Pristine
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ydes942z_Spasskaya-half.png', // Damaged
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/frtq98rv_Spasskaya-gone.png' // Destroyed
      ],
      fortifiedImages: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/2yf3islh_Russia%20landmark%20buffered-1.png', // Light fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/g10zr43t_Russia%20landmark%20buffered-2.png', // Medium fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/675ocljf_Russia%20landmark%20buffered-3.png'  // Heavy fortifications
      ],
      position: { x: 65, y: 25 } // Moscow position
    },
    CHN: {
      name: "Tiananmen Gate",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/nsb4u0vc_Tiananmen%20Gate%20-%20whole.png', // Pristine
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/rizuvks2_Tiananmen%20Gate%20-%20half.png', // Damaged
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/i76147dw_Tiananmen%20Gate%20-%20gone.png' // Destroyed
      ],
      fortifiedImages: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ku6z6x7g_China%20landmark%20buffered-1.png', // Light fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/1cd5er9n_China%20landmark%20buffered-2.png', // Medium fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/qwhfduye_China%20landmark%20buffered-3.png'  // Heavy fortifications
      ],
      position: { x: 75, y: 40 } // Beijing position
    },
    NK: {
      name: "Juche Tower",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/uqnc6prg_Juche%20Tower%20-%20whole.png', // Pristine
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/5n3wauud_Juche%20Tower%20-%20half.png', // Damaged
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/5a5v1q1h_Juche%20Tower%20-%20gone.png' // Destroyed
      ],
      fortifiedImages: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/fsxxw5ij_NKorea%20landmark%20buffered-1.png', // Light fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/x10mbf73_NKorea%20landmark%20buffered-2.png', // Medium fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/q2127llm_NKorea%20landmark%20buffered-3.png'  // Heavy fortifications
      ],
      position: { x: 82, y: 38 } // Pyongyang position
    },
    EU: {
      name: "Berlaymont Building",
      images: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/mwqyd0eh_Berlaymont%20-%20whole.png', // Pristine
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/60y6yc3a_Berlaymont%20-%20half.png', // Damaged
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/xpa92000_Berlaymont%20-%20gone.png' // Destroyed
      ],
      fortifiedImages: [
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/i3i8yhy7_EU%20landmark%20buffered-1.png', // Light fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/7ke9evvx_EU%20landmark%20buffered-2.png', // Medium fortifications
        'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/8aee671a_EU%20landmark%20buffered-3.png'  // Heavy fortifications
      ],
      position: { x: 48, y: 30 } // Brussels position
    }
  };
  
  const landmarkData = landmarks[country.code];
  
  // If no landmark data for this country, don't render
  if (!landmarkData) {
    return null;
  }
  
  // Select the appropriate image:
  // - If buffer > 0: Use fortified images (buffered-1, buffered-2, or buffered-3)
  // - If buffer = 0: Use damage-based images (pristine, damaged, destroyed)
  let imageUrl;
  if (hasBuffer && landmarkData.fortifiedImages) {
    // Use fortified sprite based on buffer level (1, 2, or 3)
    // fortificationLevel is 1 (light), 2 (medium), or 3 (heavy)
    imageUrl = landmarkData.fortifiedImages[fortificationLevel - 1];
  } else {
    // Use regular damage-based sprites
    imageUrl = landmarkData.images[stage];
  }
  
  const stageLabels = ['Pristine', 'Damaged', 'Destroyed'];
  const stageColors = ['text-green-400', 'text-orange-400', 'text-red-500'];
  
  // Fortification labels
  const fortificationLabels = ['No Defenses', 'Light Fortifications', 'Medium Fortifications', 'Heavy Fortifications'];
  const fortificationColors = ['text-gray-400', 'text-yellow-400', 'text-orange-400', 'text-red-400'];
  
  return (
    <div
      className="absolute cursor-pointer transition-all duration-300 hover:z-20 group pointer-events-auto"
      style={{
        left: `${landmarkData.position.x}%`,
        top: `${landmarkData.position.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: 5,
      }}
    >
      {/* Landmark Image - Scales and pops up on hover */}
      <div className="relative transition-all duration-300 group-hover:scale-150 group-hover:-translate-y-3">
        
        {/* Landmark or Fortified Landmark Image */}
        <img
          src={imageUrl}
          alt={`${landmarkData.name} - ${hasBuffer ? fortificationLabels[fortificationLevel] : stageLabels[stage]}`}
          className="w-24 h-24 object-contain transition-all duration-300"
          style={{
            filter: hasBuffer
              ? fortificationLevel === 3
                ? 'drop-shadow(0 0 15px rgba(239, 68, 68, 0.6)) drop-shadow(0 0 25px rgba(239, 68, 68, 0.4))' // Red glow for heavy
                : fortificationLevel === 2
                ? 'drop-shadow(0 0 15px rgba(249, 115, 22, 0.6)) drop-shadow(0 0 25px rgba(249, 115, 22, 0.4))' // Orange glow for medium
                : 'drop-shadow(0 0 15px rgba(234, 179, 8, 0.6)) drop-shadow(0 0 25px rgba(234, 179, 8, 0.4))' // Yellow glow for light
              : stage === 2 
                ? 'grayscale(0.8) brightness(0.7) drop-shadow(0 0 20px rgba(239, 68, 68, 0.8)) drop-shadow(0 0 30px rgba(239, 68, 68, 0.6))' 
                : stage === 1 
                ? 'grayscale(0.3) brightness(0.85) drop-shadow(0 0 15px rgba(249, 115, 22, 0.7)) drop-shadow(0 0 25px rgba(180, 83, 9, 0.5))' 
                : 'drop-shadow(0 0 15px rgba(34, 197, 94, 0.6)) drop-shadow(0 0 25px rgba(34, 197, 94, 0.4))',
          }}
        />
        
        {/* Landmark Label - Shows fortifications OR damage status */}
        <div className="absolute -bottom-7 left-1/2 transform -translate-x-1/2 whitespace-nowrap transition-all duration-300 group-hover:scale-110">
          <div className="bg-black/80 px-2 py-1 rounded backdrop-blur-sm transition-all duration-300 group-hover:bg-black/95 group-hover:px-3 group-hover:py-1.5"
               style={{
                 border: hasBuffer
                   ? `1px solid ${fortificationLevel === 3 ? 'rgba(239, 68, 68, 0.5)' : fortificationLevel === 2 ? 'rgba(249, 115, 22, 0.5)' : 'rgba(234, 179, 8, 0.5)'}`
                   : `1px solid ${stage === 0 ? 'rgba(34, 197, 94, 0.5)' : stage === 1 ? 'rgba(249, 115, 22, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
                 boxShadow: hasBuffer
                   ? (fortificationLevel === 3 ? '0 0 10px rgba(239, 68, 68, 0.3)' : fortificationLevel === 2 ? '0 0 10px rgba(249, 115, 22, 0.3)' : '0 0 10px rgba(234, 179, 8, 0.3)')
                   : (stage === 0 ? '0 0 10px rgba(34, 197, 94, 0.3)' : stage === 1 ? '0 0 10px rgba(249, 115, 22, 0.3)' : '0 0 10px rgba(239, 68, 68, 0.3)')
               }}
          >
            <div className="text-[10px] font-bold text-gray-300 uppercase tracking-wider">
              {landmarkData.name}
            </div>
            
            {/* Show fortification status if buffered, otherwise show damage */}
            {hasBuffer ? (
              <div className={`text-[9px] font-bold ${fortificationColors[fortificationLevel]} uppercase tracking-wider`}>
                🛡️ {fortificationLabels[fortificationLevel]} (+{bufferPercent.toFixed(0)}%)
              </div>
            ) : (
              <div className={`text-[9px] font-bold ${stageColors[stage]} uppercase tracking-wider`}>
                {stageLabels[stage]} ({populationPercent.toFixed(0)}%)
              </div>
            )}
          </div>
        </div>
        
        {/* Smoke effect for damaged/destroyed */}
        {stage > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(stage === 2 ? 3 : 2)].map((_, i) => (
              <div
                key={`smoke-${landmarkData.name}-${i}`}
                className="absolute"
                style={{
                  left: `${30 + i * 20}%`,
                  top: '-10px',
                  animation: `smoke-rise ${3 + i * 0.5}s ease-out infinite`,
                  animationDelay: `${i * 0.8}s`
                }}
              >
                <div
                  className="w-4 h-4 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(50,50,50,0.6) 0%, rgba(80,80,80,0.3) 40%, transparent 100%)',
                    filter: 'blur(3px)'
                  }}
                />
              </div>
            ))}
          </div>
        )}
        
        {/* Fire animations for destroyed landmarks */}
        {stage === 2 && (
          <div className="absolute inset-0 pointer-events-none overflow-visible">
            {/* Multiple small fires on the rubble/debris */}
            {[...Array(4)].map((_, i) => (
              <div
                key={`fire-${i}`}
                className="absolute"
                style={{
                  left: `${20 + i * 20}%`,
                  top: `${50 + (i % 2) * 15}%`,  // Middle-bottom area (50-65%)
                  animation: `fire-flicker ${0.3 + i * 0.1}s ease-in-out infinite`,
                  animationDelay: `${i * 0.15}s`
                }}
              >
                {/* Flame shape */}
                <div
                  className="w-3 h-4"
                  style={{
                    background: 'linear-gradient(to top, rgba(255, 100, 0, 0.9) 0%, rgba(255, 200, 0, 0.8) 50%, rgba(255, 255, 100, 0.4) 100%)',
                    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                    filter: 'blur(1px)',
                    transformOrigin: 'bottom center'
                  }}
                />
                {/* Orange glow */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(circle, rgba(255, 100, 0, 0.6) 0%, transparent 70%)',
                    filter: 'blur(3px)',
                    transform: 'scale(1.5)'
                  }}
                />
              </div>
            ))}
            
            {/* Larger central fire in the rubble */}
            <div
              className="absolute"
              style={{
                left: '45%',
                top: '55%',  // Middle-bottom area
                animation: 'fire-flicker 0.4s ease-in-out infinite'
              }}
            >
              <div
                className="w-4 h-5"
                style={{
                  background: 'linear-gradient(to top, rgba(255, 80, 0, 1) 0%, rgba(255, 180, 0, 0.9) 50%, rgba(255, 255, 80, 0.5) 100%)',
                  clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                  filter: 'blur(1px)',
                  transformOrigin: 'bottom center'
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(circle, rgba(255, 120, 0, 0.8) 0%, transparent 70%)',
                  filter: 'blur(4px)',
                  transform: 'scale(2)'
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes smoke-rise {
          0% {
            opacity: 0.8;
            transform: translateY(0) scale(0.5);
          }
          50% {
            opacity: 0.5;
            transform: translateY(-20px) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-40px) scale(1.5);
          }
        }
        
        @keyframes fire-flicker {
          0% {
            opacity: 1;
            transform: scaleY(1) scaleX(1);
          }
          25% {
            opacity: 0.85;
            transform: scaleY(1.1) scaleX(0.95);
          }
          50% {
            opacity: 0.9;
            transform: scaleY(0.95) scaleX(1.05);
          }
          75% {
            opacity: 0.95;
            transform: scaleY(1.05) scaleX(0.98);
          }
          100% {
            opacity: 1;
            transform: scaleY(1) scaleX(1);
          }
        }
        
        @keyframes fortification-build {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fortification-pulse {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
}
